import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Info, TriangleAlert } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getConsumptionForecast, type ForecastResponse } from '@/api/services/consumption.api';
import type { InventoryItem } from '@/api/services/inventory.api';
import { formatDate } from '@/lib/utils';

/** Horizons a planner actually thinks in. */
const HORIZONS = [
  { value: '30', label: 'next 30 days' },
  { value: '60', label: 'next 60 days' },
  { value: '90', label: 'next 90 days' },
];

const qty = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });
const rate = (n: number) => n.toFixed(n < 1 ? 4 : 2);

/**
 * How much of an item the work already on the books is expected to consume.
 *
 * The distinction this panel exists to make is between a projection and an
 * average. Three wells spudding next month is a reason to expect demand;
 * last quarter's burn rate is not, and a number derived from the second should
 * not be presented as though it came from the first. The method is stated
 * rather than implied.
 */
export function DemandForecast({ items }: { items: InventoryItem[] }) {
  const [itemId, setItemId] = useState<string>('');
  const [periods, setPeriods] = useState('60');
  const [data, setData] = useState<ForecastResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // Items are loaded by the page; pick the first so the panel opens with
  // something in it rather than an instruction to choose.
  useEffect(() => {
    if (!itemId && items.length > 0) setItemId(items[0].id);
  }, [items, itemId]);

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    setLoading(true);
    getConsumptionForecast(itemId, Number(periods))
      .then((d) => !cancelled && setData(d))
      .catch(() => !cancelled && setData(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [itemId, periods]);

  const sorted = useMemo(() => [...items].sort((a, b) => a.sku.localeCompare(b.sku)), [items]);

  const projected = data?.method === 'PLANNED_ACTIVITY';

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-base font-semibold">Expected demand</h3>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={itemId} onValueChange={setItemId}>
              <SelectTrigger className="h-8 w-full sm:w-64">
                <SelectValue placeholder="Select an item" />
              </SelectTrigger>
              <SelectContent>
                {sorted.map((i) => (
                  <SelectItem key={i.id} value={i.id}>
                    {i.sku} — {i.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={periods} onValueChange={setPeriods}>
              <SelectTrigger className="h-8 w-full sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HORIZONS.map((h) => (
                  <SelectItem key={h.value} value={h.value}>
                    {h.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {loading && <p className="text-sm text-muted-foreground">Working it out…</p>}

        {!loading && !data && (
          <p className="text-sm text-muted-foreground">No forecast available for this item.</p>
        )}

        {!loading && data && (
          <>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              {data.expectedQuantity === null ? (
                <p className="text-2xl font-semibold text-muted-foreground">—</p>
              ) : (
                <p className="text-2xl font-semibold tabular-nums">
                  {qty(data.expectedQuantity)}{' '}
                  <span className="text-base font-normal text-muted-foreground">
                    {data.item.unit}
                  </span>
                </p>
              )}
              {/* The method is the caveat. A calendar average dressed as a
                  forecast is the thing worth guarding against. */}
              <Badge
                variant="outline"
                className={
                  projected
                    ? 'border-status-success-border bg-status-success-subtle text-status-success-foreground'
                    : 'border-status-warning-border bg-status-warning-subtle text-status-warning-foreground'
                }
              >
                {projected ? 'From planned work' : 'Not from planned work'}
              </Badge>
            </div>

            <p className="text-sm text-muted-foreground">{data.basis}</p>

            {data.jobs.length > 0 && (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Job</th>
                      <th className="px-3 py-2 text-left font-medium">Starts</th>
                      <th className="px-3 py-2 text-right font-medium">Planned work</th>
                      <th className="px-3 py-2 text-right font-medium">Rate</th>
                      <th className="px-3 py-2 text-right font-medium">Expected</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.jobs.map((j) => (
                      <tr key={j.jobId}>
                        <td className="px-3 py-2">
                          <p className="font-medium">{j.wellName ?? j.jobName}</p>
                          <p className="text-xs text-muted-foreground">{j.jobNumber}</p>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {j.startDate ? formatDate(j.startDate) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {qty(j.plannedWork)} {j.workUnit}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="tabular-nums">
                            {rate(j.rateUsed)} {data.item.unit}/{j.workUnit}
                          </span>
                          {/* Which observations produced the rate decides how
                              much weight the number deserves. */}
                          <p className="text-xs text-muted-foreground">{j.rateBasis}</p>
                        </td>
                        <td className="px-3 py-2 text-right font-medium tabular-nums">
                          {qty(j.expectedQuantity)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {data.excludedJobs.length > 0 && (
              <div className="space-y-2 rounded-md border border-status-warning-border bg-status-warning-subtle/40 p-3">
                <div className="flex items-center gap-2">
                  <TriangleAlert className="h-4 w-4 text-status-warning-foreground" />
                  <h4 className="text-sm font-semibold">
                    {data.excludedJobs.length} job
                    {data.excludedJobs.length > 1 ? 's' : ''} left out of this figure
                  </h4>
                </div>
                <ul className="space-y-1">
                  {data.excludedJobs.map((j) => (
                    <li key={j.jobNumber} className="text-sm">
                      <span className="font-medium">{j.wellName ?? j.jobNumber}</span>{' '}
                      <span className="text-muted-foreground">— {j.reason}</span>
                    </li>
                  ))}
                </ul>
                <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  These wells may well consume this item. Record the work done alongside the
                  quantity, such as stages pumped or feet drilled, and the rate needed to project
                  them builds itself.
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
